'use client';

import { useRef, useEffect, useCallback } from 'react';
import { FILTER_TABS, FILTER_TAB_LABELS, type FilterTab } from '@/lib/search-categories';
import { ui, uiFb, isRtlLang } from '@/lib/i18n';

interface CommandPaletteFiltersProps {
  activeFilter: FilterTab;
  onFilterChange: (tab: FilterTab) => void;
  isFocused: boolean;
  onFocus: () => void;
  lang: string;
}

export function CommandPaletteFilters({
  activeFilter,
  onFilterChange,
  isFocused,
  onFocus,
  lang,
}: CommandPaletteFiltersProps) {
  const tabListRef = useRef<HTMLDivElement>(null);

  // Focus the active tab button when this zone is focused
  useEffect(() => {
    if (isFocused && tabListRef.current) {
      const activeBtn = tabListRef.current.querySelector<HTMLButtonElement>('[aria-selected="true"]');
      activeBtn?.focus();
    }
  }, [isFocused]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const idx = FILTER_TABS.indexOf(activeFilter);
      const rtl = isRtlLang(lang);
      // Per WAI-ARIA: in RTL, ArrowRight = previous, ArrowLeft = next
      const forwardKey = rtl ? 'ArrowLeft' : 'ArrowRight';
      const backwardKey = rtl ? 'ArrowRight' : 'ArrowLeft';
      if (e.key === forwardKey) {
        e.preventDefault();
        const next = FILTER_TABS[Math.min(idx + 1, FILTER_TABS.length - 1)];
        onFilterChange(next);
      } else if (e.key === backwardKey) {
        e.preventDefault();
        const prev = FILTER_TABS[Math.max(idx - 1, 0)];
        onFilterChange(prev);
      }
    },
    [activeFilter, onFilterChange, lang],
  );

  return (
    <div
      ref={tabListRef}
      role="tablist"
      aria-label={uiFb('ui.search.filter_results', 'Filter results', lang)}
      className="flex gap-1 overflow-x-auto border-b border-border-subtle px-4 py-2"
      onKeyDown={handleKeyDown}
      onFocus={onFocus}
    >
      {FILTER_TABS.map((tab) => {
        const label = ui(FILTER_TAB_LABELS[tab], lang);
        // Fallback label if i18n key not found
        const displayLabel = label === FILTER_TAB_LABELS[tab]
          ? tab.charAt(0).toUpperCase() + tab.slice(1)
          : label;
        return (
          <button
            key={tab}
            role="tab"
            aria-selected={activeFilter === tab}
            tabIndex={activeFilter === tab ? 0 : -1}
            onClick={() => onFilterChange(tab)}
            className={`shrink-0 rounded-full px-3 py-1 min-h-[44px] text-label font-medium transition-colors ${
              activeFilter === tab
                ? 'bg-brand text-text-on-brand'
                : 'text-text-dim hover:bg-surface-hover hover:text-text-secondary'
            }`}
          >
            {displayLabel}
          </button>
        );
      })}
    </div>
  );
}
